// stores/notificationStore.ts
import { create } from "zustand";
import { getNotifications } from "@utils/api";

/**
 * Notification item structure
 */
export interface Notification {
  id: number;
  content: string;
  dateCreated: string;
  read: boolean;
  archived?: boolean;
  [key: string]: any;
}

/**
 * Notifications response structure
 */
export interface NotificationsData {
  content: Notification[];
  [key: string]: any;
}

/**
 * Notification store for managing notification state and fetching
 */
interface NotificationStore {
  // State
  notifications: NotificationsData | null;
  hasNewNotifications: boolean;
  showOldNotifications: boolean;
  isLoading: boolean;
  error: Error | null;
  lastFetchTime: number | null;

  // Actions
  setNotifications: (notifications: NotificationsData | null) => void;
  setHasNewNotifications: (value: boolean) => void;
  setShowOldNotifications: (value: boolean) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: Error | null) => void;

  // Async actions
  fetchNotifications: (params?: { read?: boolean }) => Promise<void>;

  // Utility
  reset: () => void;
}

const getDefaultState = () => ({
  notifications: null,
  hasNewNotifications: false,
  showOldNotifications: false,
  isLoading: false,
  error: null as Error | null,
  lastFetchTime: null,
});

/**
 * Zustand store for managing notifications
 *
 * Usage:
 * ```typescript
 * const { notifications, fetchNotifications, hasNewNotifications } = useNotificationStore();
 * ```
 */
export const useNotificationStore = create<NotificationStore>()((set, get) => ({
  ...getDefaultState(),

  setNotifications: (notifications) => {
    set({ notifications, lastFetchTime: Date.now() });

    // Update hasNewNotifications based on notification content
    if (notifications?.content) {
      const hasUnread = notifications.content.some((n) => !n.read);
      set({ hasNewNotifications: hasUnread });
    }
  },

  setHasNewNotifications: (value) => set({ hasNewNotifications: value }),

  setShowOldNotifications: (value) => set({ showOldNotifications: value }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  /**
   * Fetch notifications from backend
   * This is the centralized notification fetching function
   */
  fetchNotifications: async (params = {}) => {
    const state = get();

    // Prevent duplicate fetches
    if (state.isLoading) {
      console.log(
        "[NotificationStore] Already loading, skipping duplicate fetch"
      );
      return;
    }

    console.log(
      "[NotificationStore] Fetching notifications with params:",
      params
    );

    set({ isLoading: true, error: null });

    try {
      const response = await getNotifications(params);
      const notificationsData = response?.data;

      if (notificationsData?.content) {
        // Sort by date (newest first)
        notificationsData.content.sort(
          (a: Notification, b: Notification) =>
            new Date(b.dateCreated).getTime() -
            new Date(a.dateCreated).getTime()
        );
      }

      set({
        notifications: notificationsData,
        isLoading: false,
        error: null,
        lastFetchTime: Date.now(),
      });

      // Update hasNewNotifications
      const hasUnread = notificationsData?.content?.some(
        (n: Notification) => !n.read
      );
      set({ hasNewNotifications: hasUnread ?? false });

      console.log("[NotificationStore] Fetch successful:", {
        count: notificationsData?.content?.length ?? 0,
        hasUnread,
      });
    } catch (error) {
      console.error("[NotificationStore] Fetch error:", error);
      set({
        error: error as Error,
        isLoading: false,
      });
    }
  },

  reset: () => {
    set(getDefaultState());
  },
}));
